import React, { useMemo } from 'react';
import { Card } from 'flowbite-react';
import { DependencyNodeId, DatabaseCommand } from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import DependencyNodesTable from './DependencyNodesTable';
import DependencyEdgeForm from './DependencyEdgeForm';

interface DependenciesTableProps {
  nodeId: DependencyNodeId;
  className?: string;
}

const DependenciesTable: React.FC<DependenciesTableProps> = ({ nodeId, className = '' }) => {
  const { dependencyGraph, applyCommand } = useDatabase();
  
  const { dependencies, dependents } = useMemo(() => {
    if (!dependencyGraph) {
      return { dependencies: [], dependents: [] };
    }
    
    // Get dependencies (what this node depends on)
    const dependencies = dependencyGraph.get_dependencies(nodeId);
    
    // Get dependents (what depends on this node)
    const dependents = dependencyGraph.get_dependents(nodeId);
    
    return { dependencies, dependents };
  }, [nodeId, dependencyGraph]);
  
  return (
    <div className={`space-y-6 overflow-auto ${className}`}>
      {/* Dependencies section (what this node depends on) */}
      <Card className="overflow-hidden">
        <h3 className="text-lg font-medium mb-3">Dependencies (what this depends on)</h3>
        <div className="overflow-auto">
          <DependencyNodesTable 
            nodeIds={dependencies} 
            emptyMessage="This item doesn't depend on anything" 
            onRemove={async (depId: DependencyNodeId) => {
              const cmd: DatabaseCommand = {
                type: 'SetDependency',
                dependency: { by: nodeId, on: depId, annotation: null },
                enabled: false,
              };
              await applyCommand(cmd);
            }}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-cm-border-subtle">
          <DependencyEdgeForm 
            direction="dependencyBy" 
            nodeId={nodeId} 
            placeholder="Add a dependency..." 
          />
        </div>
      </Card>
      
      {/* Dependents section (what depends on this node) */}
      <Card className="overflow-hidden">
        <h3 className="text-lg font-medium mb-3">Dependents (what depends on this)</h3>
        <div className="overflow-auto">
          <DependencyNodesTable 
            nodeIds={dependents} 
            emptyMessage="Nothing depends on this item" 
            onRemove={async (dependentId: DependencyNodeId) => {
              const cmd: DatabaseCommand = {
                type: 'SetDependency',
                dependency: { by: dependentId, on: nodeId, annotation: null },
                enabled: false,
              };
              await applyCommand(cmd);
            }}
          />
        </div>
        <div className="mt-4 pt-4 border-t border-cm-border-subtle">
          <DependencyEdgeForm 
            direction="dependencyOf" 
            nodeId={nodeId} 
            placeholder="Add a dependent..." 
          />
        </div>
      </Card>
    </div>
  );
};

export default DependenciesTable;
